import { createContext, useContext, useReducer, ReactNode } from 'react';

interface ProductConfiguration {
  productId: string;
  material: string;
  finish: string;
  color: string;
  dimensions: {
    length: number;
    height: number;
    depth: number;
  };
  price: number;
}

interface QuoteItem extends ProductConfiguration {
  quantity: number;
  name: string;
}

interface ConfiguratorState {
  currentConfiguration: ProductConfiguration | null;
  quoteItems: QuoteItem[];
}

type ConfiguratorAction =
  | { type: 'SET_CONFIGURATION'; payload: ProductConfiguration }
  | { type: 'UPDATE_CONFIGURATION'; payload: Partial<ProductConfiguration> }
  | { type: 'ADD_TO_QUOTE'; payload: QuoteItem }
  | { type: 'REMOVE_FROM_QUOTE'; payload: string }
  | { type: 'CLEAR_QUOTE' }
  | { type: 'RESET_CONFIGURATION' };

const initialState: ConfiguratorState = {
  currentConfiguration: null,
  quoteItems: [],
};

const configuratorReducer = (state: ConfiguratorState, action: ConfiguratorAction): ConfiguratorState => {
  switch (action.type) {
    case 'SET_CONFIGURATION':
      return {
        ...state,
        currentConfiguration: action.payload,
      };
    case 'UPDATE_CONFIGURATION':
      return {
        ...state,
        currentConfiguration: state.currentConfiguration
          ? { ...state.currentConfiguration, ...action.payload }
          : null,
      };
    case 'ADD_TO_QUOTE':
      return {
        ...state,
        quoteItems: [...state.quoteItems, action.payload],
      };
    case 'REMOVE_FROM_QUOTE':
      return {
        ...state,
        quoteItems: state.quoteItems.filter(item => item.productId !== action.payload),
      };
    case 'CLEAR_QUOTE':
      return {
        ...state,
        quoteItems: [],
      };
    case 'RESET_CONFIGURATION':
      return {
        ...state,
        currentConfiguration: null,
      };
    default:
      return state;
  }
};

interface ConfiguratorContextType {
  state: ConfiguratorState;
  setConfiguration: (config: ProductConfiguration) => void;
  updateConfiguration: (updates: Partial<ProductConfiguration>) => void;
  addToQuote: (item: QuoteItem) => void;
  removeFromQuote: (productId: string) => void;
  clearQuote: () => void;
  resetConfiguration: () => void;
}

const ConfiguratorContext = createContext<ConfiguratorContextType | undefined>(undefined);

export const useConfigurator = () => {
  const context = useContext(ConfiguratorContext);
  if (!context) {
    throw new Error('useConfigurator must be used within a ConfiguratorProvider');
  }
  return context;
};

interface ConfiguratorProviderProps {
  children: ReactNode;
}

export const ConfiguratorProvider = ({ children }: ConfiguratorProviderProps) => {
  const [state, dispatch] = useReducer(configuratorReducer, initialState);

  const setConfiguration = (config: ProductConfiguration) => {
    dispatch({ type: 'SET_CONFIGURATION', payload: config });
  };

  const updateConfiguration = (updates: Partial<ProductConfiguration>) => {
    dispatch({ type: 'UPDATE_CONFIGURATION', payload: updates });
  };

  const addToQuote = (item: QuoteItem) => {
    dispatch({ type: 'ADD_TO_QUOTE', payload: item });
  };

  const removeFromQuote = (productId: string) => {
    dispatch({ type: 'REMOVE_FROM_QUOTE', payload: productId });
  };

  const clearQuote = () => {
    dispatch({ type: 'CLEAR_QUOTE' });
  };

  const resetConfiguration = () => {
    dispatch({ type: 'RESET_CONFIGURATION' });
  };

  const value = {
    state,
    setConfiguration,
    updateConfiguration,
    addToQuote,
    removeFromQuote,
    clearQuote,
    resetConfiguration,
  };

  return (
    <ConfiguratorContext.Provider value={value}>
      {children}
    </ConfiguratorContext.Provider>
  );
};